import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  FormControl,
  Typography,
  makeStyles,
  Theme,
  DefaultButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
  Box,
  Chip,
  Divider,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
  FormHelperText,
  BbThemeProvider as ThemeProvider,
} from '@bb-ui/react-library';
import { Clear, Add, Time, Globe, Warning } from '@bb-ui/icons/dist/small';
import { toZonedTime } from 'date-fns-tz';
import { enUS } from 'date-fns/locale';
import { DatePicker } from '@bb-ui/lab/dist/DatePicker';
// eslint-disable-next-line import-x/no-extraneous-dependencies
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
// eslint-disable-next-line import-x/no-extraneous-dependencies
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import { useCohortCreation } from '../hooks/useCohortCreation';
import FilterSitesDialog from '../shared/FilterSitesDialog';
import ScheduleConflictDialog from './ScheduleConflictDialog';
import { useCohortsActions } from '../hooks/useCohortsActions';
import { useCohortsContext } from 'contexts/CohortsContext/CohortsContext';
import { combineDateAndTime, extractTimeString, formatDateToLocalString } from '../utils';

type TimezoneOption = 'LOCAL' | 'UTC' | 'IST' | 'ET';

const TIMEZONE_MAP: Record<Exclude<TimezoneOption, 'LOCAL'>, string> = {
  UTC: 'UTC',
  IST: 'Asia/Kolkata',
  ET: 'America/New_York',
};

const getNextValidDate = (minDays: number): Date => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + minDays);
  return d;
};

const createDateFromTz = (dateString: string, timeZone: string): Date => {
  const testDate = new Date(`${dateString}Z`);
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    timeZoneName: 'shortOffset',
  });
  const parts = formatter.formatToParts(testDate);
  const offsetPart = parts.find((p) => p.type === 'timeZoneName')?.value;

  let offset = 'Z';
  if (offsetPart && offsetPart !== 'GMT') {
    const rawOffset = offsetPart.replace('GMT', '');
    const sign = rawOffset[0];
    const timeParts = rawOffset.substring(1).split(':');
    const h = timeParts[0].padStart(2, '0');
    const m = timeParts[1] ? timeParts[1].padStart(2, '0') : '00';
    offset = `${sign}${h}:${m}`;
  }

  return new Date(`${dateString}:00${offset}`);
};

const useStyles = makeStyles((theme: Theme) => ({
  dialogPaper: {
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  dialogContent: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: theme.spacing(3),
  },
  formWrapper: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden',
    minHeight: 0,
  },
  formControl: {
    margin: theme.spacing(2, 0, 0, 0),
    minWidth: '100%',
  },
  siteSelectionContainer: {
    margin: theme.spacing(3, 0),
  },
  siteButtonWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(2),
  },
  selectSitesButton: {
    minHeight: '48px',
    borderRadius: theme.shape.borderRadius * 2,
    border: `2px dashed ${theme.palette.primary.main}`,
    backgroundColor: 'transparent',
    color: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: `${theme.palette.primary.light}10`,
      borderColor: theme.palette.primary.dark,
    },
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  siteTableContainer: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius * 2,
    maxHeight: '300px',
    overflowY: 'auto',
    width: '100%',
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
  },
  siteTable: {
    width: '100%',
    '& .MuiTableCell-root': {
      padding: theme.spacing(1.5, 2),
      fontSize: '0.875rem',
      borderBottom: `1px solid ${theme.palette.divider}`,
      height: '56px',
    },
    '& .MuiTableHead-root .MuiTableCell-root': {
      fontWeight: 600,
      backgroundColor: theme.palette.grey[50],
      color: theme.palette.text.primary,
      padding: theme.spacing(1.5, 2),
      height: '56px',
    },
  },
  emptyStateContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '120px',
    padding: theme.spacing(3),
    color: theme.palette.text.secondary,
    textAlign: 'center',
  },
  emptyStateIcon: {
    fontSize: '48px',
    marginBottom: theme.spacing(1),
    opacity: 0.5,
  },
  captainIdCell: {
    width: '140px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  urlCell: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  actionCell: {
    width: '80px',
    minWidth: '80px',
    textAlign: 'center' as const,
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    backgroundColor: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.divider}`,
    position: 'sticky',
    bottom: 0,
    zIndex: 1,
    gap: theme.spacing(2),
    justifyContent: 'flex-end',
  },
  createButton: {
    minHeight: '48px',
    minWidth: '140px',
    borderRadius: theme.shape.borderRadius * 2,
    fontSize: '0.95rem',
    fontWeight: 600,
    textTransform: 'none',
  },
  cancelButton: {
    minHeight: '48px',
    minWidth: '100px',
    borderRadius: theme.shape.borderRadius * 2,
    fontWeight: 600,
    textTransform: 'none',
    color: theme.palette.text.secondary,
    backgroundColor: 'transparent',
    border: `1px solid ${theme.palette.divider}`,
    '&:hover': {
      backgroundColor: theme.palette.grey[100],
      borderColor: theme.palette.grey[400],
    },
  },
  siteCountChip: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontSize: '0.75rem',
    fontWeight: 600,
    marginLeft: theme.spacing(1),
  },
  formField: {
    '& .MuiInputLabel-root': {
      fontSize: '0.95rem',
      fontWeight: 500,
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.shape.borderRadius * 2,
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
      },
    },
  },
  infoSection: {
    backgroundColor: `${theme.palette.info.light}10`,
    border: `1px solid ${theme.palette.info.light}`,
    borderRadius: theme.shape.borderRadius * 2,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  infoIcon: {
    color: theme.palette.info.main,
    marginRight: theme.spacing(1),
  },
  dialogTitle: {
    '& .MuiTypography-root': {
      fontSize: '1.5rem',
      fontWeight: 600,
      color: theme.palette.text.primary,
    },
  },
  removeButton: {
    color: theme.palette.error.main,
  },
  readOnlyFieldsContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  readOnlyFieldsRow: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  readOnlyField: {
    flex: 1,
  },
  sectionDivider: {
    margin: theme.spacing(3, 0),
  },
  checkboxControl: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  dateTimeContainer: {
    position: 'relative',
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1),
  },
  dateTimeRow: {
    display: 'flex',
    gap: theme.spacing(1.5),
    alignItems: 'flex-start',
  },
  datePickerWrapper: {
    flex: 2,
    minWidth: 0,
  },
  datePickerPaper: {
    width: '262px !important',
    '& div[role="grid"]': {
      display: 'block !important',
    },
    '& .MuiCalendarPicker-viewTransitionContainer': {
      width: '238px !important',
    },
    '& .MuiPickersDay-root': {
      width: '32px !important',
      height: '32px !important',
      borderRadius: '2px !important',
      margin: '0 !important',
      fontSize: '0.875rem',
    },
    '& .MuiPickersDay-root.Mui-selected': {
      backgroundColor: `${theme.palette.brand.main} !important`,
      color: `${theme.palette.brand.contrastText} !important`,
      fontWeight: 'bold !important',
      '&:hover, &:focus': {
        backgroundColor: `${theme.palette.brand.main} !important`,
      },
    },
    '& .MuiPickersDay-today:not(.Mui-selected)': {
      color: `${theme.palette.brand.main} !important`,
      fontWeight: 'bold !important',
      border: 'none !important',
    },
  },
  timeField: {
    flex: 1,
    minWidth: 0,
  },
  timezoneRow: {
    display: 'flex',
    alignItems: 'flex-start',
    marginTop: theme.spacing(1.5),
    width: '100%',
  },
  timezoneToggleLabel: {
    fontWeight: 500,
    marginRight: theme.spacing(2),
    marginTop: '6px',
    whiteSpace: 'nowrap',
  },
  timezoneControlContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
  },
  timezoneHelperText: {
    margin: '4px 0 0 0',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'block',
    width: '100%',
  },
}));

export interface CreateCohortDialogProps {
  open: boolean;
  onClose: () => void;
  onCreateSuccess?: () => void;
  minDaysFromToday?: number;
}

const CreateCohortDialog: React.FC<CreateCohortDialogProps> = ({
  open,
  onClose,
  onCreateSuccess,
  minDaysFromToday = 15,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { createCohort, loadingCreate } = useCohortsActions();
  const { selectedRegion } = useCohortsContext();

  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [showConflictDialog, setShowConflictDialog] = useState(false);
  const [wasSubmitting, setWasSubmitting] = useState(false);
  const [selectedViewTz, setSelectedViewTz] = useState<TimezoneOption>('LOCAL');
  const [dayOfWeekError, setDayOfWeekError] = useState<string>('');

  const prevLoadingCreateRef = useRef<boolean>(false);

  const {
    cohortName,
    upgradeDate,
    selectedSites,
    formErrors,
    suggestedDate,
    enableNotifications,
    conflictingSites,
    handleCohortNameChange,
    handleUpgradeDateChange,
    handleEnableNotificationsChange,
    toggleSiteSelection,
    resetForm,
    selectedSitesArray,
    selectedSitesCount,
    validateAndPrepare,
    getUserTimezoneInfo,
  } = useCohortCreation({
    minDaysFromToday,
  });

  const { timezone } = getUserTimezoneInfo();
  const tooltipText = t('cohorts.enableNotificationsHelpText');

  const getActualTimezone = useCallback(
    (tz: TimezoneOption) => (tz === 'LOCAL' ? timezone : TIMEZONE_MAP[tz]),
    [timezone],
  );

  const safeMinDate = useMemo(() => getNextValidDate(minDaysFromToday), [minDaysFromToday]);

  const [localDate, setLocalDate] = useState<Date | null>(null);
  const [localTime, setLocalTime] = useState<string>('02:00');

  useEffect(() => {
    const baseDate = upgradeDate || suggestedDate;
    const finalDate = baseDate && baseDate < safeMinDate ? safeMinDate : baseDate;

    if (finalDate) {
      try {
        const targetTimezone = getActualTimezone(selectedViewTz);
        const zonedDate = toZonedTime(finalDate, targetTimezone);
        if (!Number.isNaN(zonedDate.getTime())) {
          setLocalDate(zonedDate);
          setLocalTime(extractTimeString(zonedDate));
        }
      } catch {
        setLocalDate(safeMinDate);
        setLocalTime('02:00');
      }
    } else {
      setLocalDate(safeMinDate);
      setLocalTime('02:00');
    }
  }, [upgradeDate, selectedViewTz, getActualTimezone, suggestedDate, safeMinDate]);

  const datePickerStrings = useMemo(
    () => ({
      monthLabel: t('cohorts.month', 'Month'),
      monthNextLabel: t('cohorts.nextMonth', 'Next month'),
      monthPreviousLabel: t('cohorts.previousMonth', 'Previous month'),
      selectedDayLabel: (day: string) => `${day} - ${t('cohorts.selected', 'Selected')}`,
      yearLabel: t('cohorts.year', 'Year'),
    }),
    [t],
  );

  const updateUpgradeFromParts = useCallback(
    (date: Date | null, time: string) => {
      if (!date || Number.isNaN(date.getTime()) || !time) return;
      const combined = combineDateAndTime(date, time);
      if (!combined || Number.isNaN(combined.getTime())) return;

      let absoluteDate: Date;
      try {
        const dateStr = formatDateToLocalString(combined);
        absoluteDate = createDateFromTz(dateStr, getActualTimezone(selectedViewTz));
      } catch {
        return;
      }
      if (Number.isNaN(absoluteDate.getTime())) return;

      setDayOfWeekError('');
      handleUpgradeDateChange(absoluteDate);
    },
    [selectedViewTz, getActualTimezone, handleUpgradeDateChange],
  );

  const handleDatePickerChange = useCallback(
    (newDate: Date | null) => {
      setLocalDate(newDate);
      setDayOfWeekError('');
      if (newDate && !Number.isNaN(newDate.getTime()) && localTime) {
        updateUpgradeFromParts(newDate, localTime);
      }
    },
    [localTime, updateUpgradeFromParts],
  );

  const handleTimeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalTime(e.target.value);
  }, []);

  const handleTimeBlur = useCallback(() => {
    if (localDate && !Number.isNaN(localDate.getTime()) && localTime) {
      updateUpgradeFromParts(localDate, localTime);
    }
  }, [localDate, localTime, updateUpgradeFromParts]);

  const handleDatePickerOpen = useCallback(() => {
    const targetDate = upgradeDate || suggestedDate;
    const finalDate = targetDate && targetDate < safeMinDate ? safeMinDate : targetDate;

    if (finalDate && !Number.isNaN(finalDate.getTime())) {
      handleUpgradeDateChange(finalDate);
    }
  }, [upgradeDate, suggestedDate, safeMinDate, handleUpgradeDateChange]);

  const handleTimezoneChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, newTz: TimezoneOption | null) => {
      if (newTz !== null && !loadingCreate) {
        setSelectedViewTz(newTz);
      }
    },
    [loadingCreate],
  );

  const timezoneHelperText = selectedViewTz === 'LOCAL' ? timezone : '';

  const cohortDestination = useMemo(() => {
    if (selectedSitesCount === 0) return null;
    const firstSite = selectedSitesArray[0];
    if (!firstSite.fleet_name || !firstSite.aws_account) return null;
    return {
      fleet_id: firstSite.fleet_name,
      fleet_account_id: firstSite.aws_account,
    };
  }, [selectedSitesArray, selectedSitesCount]);

  useEffect(() => {
    const prevLoading = prevLoadingCreateRef.current;
    const currentLoading = loadingCreate;
    if (prevLoading && !currentLoading && wasSubmitting) {
      setWasSubmitting(false);
      onCreateSuccess?.();
    }
    prevLoadingCreateRef.current = currentLoading;
  }, [loadingCreate, wasSubmitting, onCreateSuccess]);

  useEffect(() => {
    if (!open) {
      resetForm();
      setWasSubmitting(false);
      setShowConflictDialog(false);
      setSelectedViewTz('LOCAL');
      setLocalDate(null);
      setLocalTime('02:00');
      setDayOfWeekError('');
    }
  }, [open, resetForm]);

  useEffect(() => {
    if (showConflictDialog && conflictingSites.length === 0) {
      setShowConflictDialog(false);
    }
  }, [conflictingSites.length, showConflictDialog]);

  const dialogMaxWidth: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false = useMemo(
    () => (selectedSitesCount === 0 ? 'md' : 'lg'),
    [selectedSitesCount],
  );

  const handleClose = useCallback(() => {
    if (!loadingCreate) {
      onClose();
    }
  }, [onClose, loadingCreate]);

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();

      if (loadingCreate || dayOfWeekError) return;

      const result = validateAndPrepare();
      if (!result || !result.isValid || !result.cohortData || !result.siteIds) return;

      if (conflictingSites.length > 0) {
        setShowConflictDialog(true);
        return;
      }

      if (!cohortDestination?.fleet_id || !cohortDestination?.fleet_account_id) return;

      const enhancedCohortData = {
        ...result.cohortData,
        fleet_id: cohortDestination.fleet_id,
        fleet_account_id: cohortDestination.fleet_account_id,
        fleet_region: selectedRegion,
      };

      try {
        setWasSubmitting(true);
        await createCohort(enhancedCohortData, result.siteIds);
      } catch {
        setWasSubmitting(false);
      }
    },
    [
      loadingCreate,
      dayOfWeekError,
      validateAndPrepare,
      conflictingSites.length,
      cohortDestination,
      selectedRegion,
      createCohort,
    ],
  );

  const combinedDateError = dayOfWeekError || formErrors.upgradeDate;

  return (
    <>
      <Dialog
        open={open}
        onClose={!loadingCreate ? handleClose : undefined}
        maxWidth={dialogMaxWidth}
        fullWidth
        disableEnforceFocus={true}
        PaperProps={{
          className: classes.dialogPaper,
        }}
      >
        <DialogTitle
          onClose={!loadingCreate ? handleClose : undefined}
          className={classes.dialogTitle}
          closeLabel={t('global.close')}
        >
          {t('cohorts.createNew')}
        </DialogTitle>
        <form onSubmit={handleSubmit} noValidate className={classes.formWrapper}>
          <DialogContent className={classes.dialogContent}>
            <Box className={classes.infoSection}>
              <Box display="flex" alignItems="center">
                <Time className={classes.infoIcon} />
                <Typography variant="body2" color="textSecondary">
                  {t('cohorts.createInfo')}
                </Typography>
              </Box>
            </Box>

            <TextField
              autoFocus
              id="cohort-name"
              label={t('cohorts.cohortName')}
              fullWidth
              value={cohortName}
              onChange={(e) => handleCohortNameChange(e.target.value)}
              error={!!formErrors.cohortName}
              helperText={formErrors.cohortName}
              className={classes.formField}
              disabled={loadingCreate}
            />

            <div className={classes.dateTimeContainer}>
              <Typography variant="subtitle2" color="textSecondary" style={{ marginBottom: 8 }}>
                {t('cohorts.upgradeDateTime', 'Upgrade Date & Time')} (
                {selectedViewTz === 'LOCAL' ? t('cohorts.local') : selectedViewTz})
              </Typography>

              <FormHelperText style={{ marginBottom: 12, marginTop: 4 }}>
                {t('cohorts.datePickerHelperText')}
              </FormHelperText>

              <div className={classes.dateTimeRow}>
                <div className={classes.datePickerWrapper}>
                  <ThemeProvider theme="light">
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enUS}>
                      <DatePicker
                        id="upgrade-date-picker"
                        label=""
                        value={localDate}
                        onChange={handleDatePickerChange}
                        onOpen={handleDatePickerOpen}
                        shouldDisableDate={(date) => date < safeMinDate}
                        disabled={loadingCreate}
                        strings={datePickerStrings}
                        format="MM/dd/yyyy"
                        PopperProps={{
                          disablePortal: false,
                          style: { zIndex: 1301 },
                        }}
                        PaperProps={{
                          className: classes.datePickerPaper,
                        }}
                        TextFieldProps={{
                          error: !!combinedDateError,
                          fullWidth: true,
                          className: classes.formField,
                        }}
                      />
                    </LocalizationProvider>
                  </ThemeProvider>
                </div>

                <TextField
                  id="upgrade-time"
                  type="time"
                  value={localTime}
                  onChange={handleTimeChange}
                  onBlur={handleTimeBlur}
                  disabled={loadingCreate}
                  error={!!combinedDateError}
                  InputLabelProps={{ shrink: true }}
                  className={`${classes.formField} ${classes.timeField}`}
                  inputProps={{ step: 300 }}
                />
              </div>

              {combinedDateError && (
                <FormHelperText error style={{ marginTop: 4 }}>
                  {combinedDateError}
                </FormHelperText>
              )}

              <div className={classes.timezoneRow}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  className={classes.timezoneToggleLabel}
                >
                  {t('cohorts.timezone')}
                </Typography>

                <div className={classes.timezoneControlContainer}>
                  <ToggleButtonGroup
                    value={selectedViewTz}
                    exclusive
                    onChange={handleTimezoneChange}
                    size="small"
                    style={{ display: 'flex', width: '100%' }}
                  >
                    <ToggleButton value="LOCAL" disabled={loadingCreate} style={{ flex: 1 }}>
                      {t('cohorts.local')}
                    </ToggleButton>
                    <ToggleButton value="UTC" disabled={loadingCreate} style={{ flex: 1 }}>
                      {t('cohorts.utc', 'UTC')}
                    </ToggleButton>
                    <ToggleButton value="IST" disabled={loadingCreate} style={{ flex: 1 }}>
                      {t('cohorts.ist', 'IST')}
                    </ToggleButton>
                    <ToggleButton value="ET" disabled={loadingCreate} style={{ flex: 1 }}>
                      {t('cohorts.et', 'ET')}
                    </ToggleButton>
                  </ToggleButtonGroup>

                  {timezoneHelperText && (
                    <FormHelperText className={classes.timezoneHelperText}>
                      {timezoneHelperText}
                    </FormHelperText>
                  )}
                </div>
              </div>
            </div>

            <FormControl className={`${classes.formControl}`}>
              <div className={classes.checkboxControl}>
                <Checkbox
                  id="cohort-notifications-enabled"
                  disabled={loadingCreate}
                  defaultChecked={true}
                  onChange={(e) => handleEnableNotificationsChange(e.target.checked)}
                  inputProps={{ 'aria-label': 'controlled' }}
                />
                <label htmlFor="cohort-notifications-enabled">
                  {t('cohorts.enableNotificationsLabel')}
                </label>
                {!enableNotifications && (
                  <Tooltip title={tooltipText}>
                    <IconButton>
                      <Warning color="error" />
                    </IconButton>
                  </Tooltip>
                )}
              </div>
            </FormControl>

            {selectedSitesCount > 0 && cohortDestination && (
              <div className={classes.readOnlyFieldsContainer}>
                <div className={classes.readOnlyFieldsRow}>
                  <TextField
                    id="fleet-id-readonly"
                    label={t('cohorts.fleetId')}
                    value={cohortDestination.fleet_id}
                    disabled
                    fullWidth
                    className={`${classes.formField} ${classes.readOnlyField}`}
                  />
                  <TextField
                    id="aws-account-readonly"
                    label={t('cohorts.awsAccount')}
                    value={cohortDestination.fleet_account_id}
                    disabled
                    fullWidth
                    className={`${classes.formField} ${classes.readOnlyField}`}
                  />
                </div>
                <div className={classes.readOnlyFieldsRow}>
                  <TextField
                    id="fleet-region-readonly"
                    label={t('cohorts.fleetRegion')}
                    value={selectedRegion}
                    disabled
                    fullWidth
                    className={`${classes.formField} ${classes.readOnlyField}`}
                  />
                </div>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  style={{ display: 'block', marginTop: 8, fontStyle: 'italic' }}
                >
                  {t('cohorts.unifiedFromSites')}
                </Typography>
              </div>
            )}

            <Divider className={classes.sectionDivider} />

            <div className={classes.siteSelectionContainer}>
              <div className={classes.siteButtonWrapper}>
                <DefaultButton
                  onClick={() => setFilterDialogOpen(true)}
                  className={classes.selectSitesButton}
                  startIcon={<Add />}
                  disabled={loadingCreate}
                >
                  {t('cohorts.selectSites')}
                  {selectedSitesCount > 0 && (
                    <Chip
                      label={selectedSitesCount}
                      size="small"
                      className={classes.siteCountChip}
                    />
                  )}
                </DefaultButton>
                {formErrors.sites && (
                  <Typography variant="caption" color="error" style={{ marginTop: 8 }}>
                    {formErrors.sites}
                  </Typography>
                )}
              </div>

              {selectedSitesCount > 0 ? (
                <Paper className={classes.siteTableContainer}>
                  <Table className={classes.siteTable}>
                    <TableHead>
                      <TableRow>
                        <TableCell className={classes.captainIdCell}>
                          {t('cohorts.captainId')}
                        </TableCell>
                        <TableCell className={classes.urlCell}>{t('cohorts.learnUrl')}</TableCell>
                        <TableCell className={classes.actionCell}>
                          {t('cohorts.removeSite')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedSitesArray.map((site) => (
                        <TableRow key={site.captain_id}>
                          <TableCell className={classes.captainIdCell}>
                            <Typography variant="body2" style={{ fontWeight: 500 }}>
                              {site.captain_id}
                            </Typography>
                          </TableCell>
                          <TableCell className={classes.urlCell}>
                            <Typography variant="body2" color="textSecondary">
                              {site.learn_url || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell className={classes.actionCell}>
                            <IconButton
                              aria-label={t('global.remove')}
                              onClick={() => toggleSiteSelection(site.captain_id)}
                              className={classes.removeButton}
                              size="small"
                              disabled={loadingCreate}
                            >
                              <Clear />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              ) : (
                <Box className={classes.emptyStateContainer}>
                  <Globe className={classes.emptyStateIcon} />
                  <Typography variant="body2" color="textSecondary">
                    {t('cohorts.noSitesSelected')}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {t('cohorts.selectSitesHint')}
                  </Typography>
                </Box>
              )}
            </div>
          </DialogContent>

          <DialogActions className={classes.dialogActions}>
            <DefaultButton
              onClick={handleClose}
              disabled={loadingCreate}
              className={classes.cancelButton}
            >
              {t('global.cancel')}
            </DefaultButton>

            {selectedSitesCount > 0 && (
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={
                  loadingCreate ||
                  !!dayOfWeekError ||
                  !cohortDestination?.fleet_id ||
                  !cohortDestination?.fleet_account_id
                }
                className={classes.createButton}
              >
                {loadingCreate ? t('cohorts.creating') : t('cohorts.create')}
              </Button>
            )}
          </DialogActions>
        </form>
      </Dialog>

      <FilterSitesDialog
        open={filterDialogOpen && !loadingCreate}
        onClose={() => setFilterDialogOpen(false)}
        selectedSiteIds={new Set(selectedSites.keys())}
        onToggleSite={toggleSiteSelection}
        timeZone={selectedViewTz === 'LOCAL' ? undefined : getActualTimezone(selectedViewTz)}
      />

      <ScheduleConflictDialog
        open={showConflictDialog}
        onClose={() => setShowConflictDialog(false)}
        conflictingSites={conflictingSites}
        onRemoveSite={(siteId) => toggleSiteSelection(siteId)}
        timezone={getActualTimezone(selectedViewTz)}
        timezoneLabel={selectedViewTz === 'LOCAL' ? 'Local' : selectedViewTz}
      />
    </>
  );
};

export default CreateCohortDialog;
